import { useState, useRef, useEffect } from 'react';
import { SelectProps } from '@amzn/awsui-components-react';
import { ApprovalState, CommandExecutionResponse } from '../types';
import { useTimer } from './useTimer';
import { getCommandVariables } from '../utils';

export const useApprovalSystem = (executeCommand: Function) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvalState, setApprovalState] = useState<ApprovalState>({ status: 'none' });
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [commandOutput, setCommandOutput] = useState('');

  const { totalTime, timeRemaining, startTimer, stopTimer } = useTimer();
  const pollingInterval = useRef<NodeJS.Timeout>();

  const handleApprovalResponse = async (response: any) => {
    if (!response) {
      setError('Invalid response received');
      return;
    }

    if (response.success || (response.data && !response.error)) {
      console.log('Command executed successfully');
      setApprovalState({ status: 'Approved' });
      setCommandOutput(JSON.stringify(response.data || response, null, 2));
      setCurrentRequestId(null);
      stopPolling();
      return;
    }

    const approvalData = response.error?.extensions?.response || response.data?.extensions?.response;
    const responseBody = approvalData?.body;
    const responseHeaders = approvalData?.headers;
    const mechanicProtocol = responseHeaders?.['x-mechanic-protocol'];
    const mechanicRequestId = responseHeaders?.['x-mechanic-requestid'];

    let newState: ApprovalState = { status: 'none' };

    switch (mechanicProtocol) {
      case "ApprovalRequired":
        newState = {
          status: 'ApprovalRequired',
          approvers: responseBody.approvers,
          approverGroups: responseBody.approver_groups || [],
          message: responseBody.message || 'Approval required',
          isBypassable: false,
          bypassActions: responseBody.bypass_actions || []
        };
        break;

      case "ApprovalPending":
        if (mechanicRequestId) {
          console.log('Got mechanic request ID:', mechanicRequestId);
          setCurrentRequestId(mechanicRequestId);
        }

        if (!responseBody.approval_id) {
          setError('Invalid approval response: Missing approval ID');
          return;
        }

        newState = {
          status: 'ApprovalPending',
          message: responseBody.message,
          approvalId: responseBody.approval_id,
          approvalUrl: responseBody.approval_url,
          secondsRemaining: Math.floor(responseBody.seconds_remaining || 43200),
          isBypassable: false,
          bypassActions: responseBody.bypass_actions || [],
        };

        startTimer(Math.floor(responseBody.seconds_remaining || 43200));
        break;

      case "ApprovalDenied":
        newState = {
          status: 'ApprovalDenied',
          message: responseBody.message,
        };
        stopPolling();
        break;

      case "ApprovalRequestExpired":
      case "ApprovalResponseExpired":
        newState = {
          status: mechanicProtocol as ApprovalState['status'],
          message: responseBody.message
        };
        stopPolling();
        break;

      case "ApprovalInternalError":
        newState = {
          status: 'ApprovalInternalError',
          message: responseBody.message,
          isBypassable: false,
          bypassActions: responseBody.bypass_actions || [],
        };
        stopPolling();
        break;

      case "Error":
        if (responseBody.exc_type === "ApprovalRequestClosed") {
          newState = {
            status: 'Approved',
            message: 'ApprovalRequestClosed - Command execution in progress. Do not refresh browser.',
          };
        }
        stopPolling();
        break;

      default:
        console.error('Unhandled response structure:', response);
        setError('Invalid response received');
        break;
    }

    setApprovalState(newState);
  };

  const submitApprovalRequest = async (
    selectedAPI: SelectProps.Option | null,
    selectedInstance: SelectProps.Option | null,
    domainIdentifier: string,
    approverId: string
  ) => {
    try {
      setLoading(true);
      setError('');

      const variables = getCommandVariables(selectedAPI, selectedInstance, domainIdentifier, 'submit', { approverId });
      const response: CommandExecutionResponse = await executeCommand({ variables });

      console.log('Full response:', JSON.stringify(response, null, 2));

      const mechanicRequestId =
        response?.data?.executeCommand?.error?.extensions?.response?.headers?.['x-mechanic-requestid'] ||
        response?.extensions?.response?.headers?.['x-mechanic-requestid'];

      if (mechanicRequestId) {
        setCurrentRequestId(mechanicRequestId);
      }

      if (response?.data?.executeCommand) {
        await handleApprovalResponse(response.data.executeCommand);
      }
    } catch (error) {
      console.error('Submit Error:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit approval request');
    } finally {
      setLoading(false);
    }
  };

  const checkApprovalStatus = async (
    selectedAPI: SelectProps.Option | null,
    selectedInstance: SelectProps.Option | null,
    domainIdentifier: string,
    approvalId: string,
    requestId: string
  ) => {
    try {
      const variables = getCommandVariables(selectedAPI, selectedInstance, domainIdentifier, 'check', { approvalId });
      const response: CommandExecutionResponse = await executeCommand({
        variables,
        context: {
          headers: {
            'x-mechanic-requestid': requestId
          }
        }
      });

      const mechanicRequestId =
        response?.data?.executeCommand?.error?.extensions?.response?.headers?.['x-mechanic-requestid'] ||
        response?.extensions?.response?.headers?.['x-mechanic-requestid'];

      if (requestId && mechanicRequestId !== requestId) {
        setError(`Mechanic request ID mismatch: ${mechanicRequestId} vs ${requestId}`);
        stopPolling();
        return;
      }

      if (response?.data?.executeCommand) {
        await handleApprovalResponse(response.data.executeCommand);
      }
    } catch (error) {
      console.error('Check Status Error:', error);
      setError(error instanceof Error ? error.message : 'Failed to check approval status');
      stopPolling();
    }
  };

  const bypassApproval = async (
    selectedAPI: SelectProps.Option | null,
    selectedInstance: SelectProps.Option | null,
    domainIdentifier: string
  ) => {
    if (!approvalState.approvalId) {
      setError('No approval ID available for bypass');
      return;
    }

    try {
      const variables = getCommandVariables(selectedAPI, selectedInstance, domainIdentifier, 'return', {
        approvalId: approvalState.approvalId
      });

      const response = await executeCommand({
        variables: {
          ...variables,
          approval: {
            ...variables.approval,
            bypass: true
          }
        }
      });

      if (response?.data?.executeCommand) {
        await handleApprovalResponse(response.data.executeCommand);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to bypass approval');
    }
  };

  const startPolling = (
    selectedAPI: SelectProps.Option | null,
    selectedInstance: SelectProps.Option | null,
    domainIdentifier: string,
    approvalId: string,
    requestId: string
  ) => {
    if (!approvalId || !requestId) {
      console.error('Missing required IDs for polling');
      return;
    }

    if (isPolling) {
      console.log('Already polling, skipping...');
      return;
    }

    setIsPolling(true);
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }

    pollingInterval.current = setInterval(() => {
      checkApprovalStatus(selectedAPI, selectedInstance, domainIdentifier, approvalId, requestId);
    }, 5000);
  };

  const stopPolling = () => {
    console.log('Stopping polling and timer');
    setIsPolling(false);
    stopTimer();

    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = undefined;
    }
  };

  useEffect(() => {
    return () => {
      stopPolling();
      setCommandOutput('');
    };
  }, []);

  return {
    loading,
    error,
    setError,
    approvalState,
    currentRequestId,
    setCurrentRequestId,
    isPolling,
    timeRemaining,
    totalTime,
    commandOutput,
    setCommandOutput,
    submitApprovalRequest,
    checkApprovalStatus,
    bypassApproval,
    startPolling,
    stopPolling
  };
};
